// FORGED Ed Systems — DEPLOY010 — v0.10.0 — DashboardPage.tsx
// Last touched: 2026-05-21
//
// Authenticated landing page. Shows teacher profile + a list of their past
// strategic plans pulled from Supabase (RLS-gated to their own account).

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { supabase } from "./supabaseClient";

type TeacherProfile = {
  full_name: string;
  school_name: string;
  pilot_code_redeemed: string | null;
};

type ReportListRow = {
  report_id: string;
  evaluation_curriculum: string;
  operating_mode: string;
  status: "queued" | "running" | "complete" | "failed";
  nickname: string | null;
  archived: boolean;
  generated_at: string;
};

const CURRICULUM_LABELS: Record<string, string> = {
  aws_sense: "AWS SENSE",
  nccer_welding: "NCCER Welding",
  american_welding_program: "AWP (American Welding Program)",
  tx_teks_teacher_generated: "TX TEKS — Teacher-Generated",
  forged_code_first: "FORGED Code-First (pilot)",
};

const OPERATING_MODE_LABELS: Record<string, string> = {
  start_of_year: "Start of year",
  mid_year: "Mid-year recovery",
  cert_prep_window: "Cert prep window",
};

const STATUS_LABELS: Record<string, { text: string; className: string }> = {
  queued: { text: "Queued…", className: "status-pill status-queued" },
  running: { text: "Generating…", className: "status-pill status-running" },
  complete: { text: "Ready", className: "status-pill status-complete" },
  failed: { text: "Failed", className: "status-pill status-failed" },
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function DashboardPage() {
  const { session, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<TeacherProfile | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [reports, setReports] = useState<ReportListRow[] | null>(null);
  const [reportsError, setReportsError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    if (!loading && !session) {
      navigate("/sign-in");
    }
  }, [loading, session, navigate]);

  // Load teacher profile.
  useEffect(() => {
    if (!session) return;
    var cancelled = false;
    supabase
      .from("teacher_accounts")
      .select("full_name, school_name, pilot_code_redeemed")
      .eq("auth_user_id", session.user.id)
      .single()
      .then((res) => {
        if (cancelled) return;
        if (res.error) {
          setProfileError(res.error.message);
          return;
        }
        setProfile(res.data as TeacherProfile);
      });
    return () => {
      cancelled = true;
    };
  }, [session]);

  // Load reports. Refetched whenever showArchived toggles.
  useEffect(() => {
    if (!session) return;
    var cancelled = false;

    var query = supabase
      .from("reports")
      .select("report_id, evaluation_curriculum, operating_mode, status, nickname, archived, generated_at")
      .order("generated_at", { ascending: false });

    if (!showArchived) {
      query = query.eq("archived", false);
    }

    query.then((res) => {
      if (cancelled) return;
      if (res.error) {
        setReportsError(res.error.message);
        return;
      }
      setReports((res.data as ReportListRow[]) || []);
    });
    return () => {
      cancelled = true;
    };
  }, [session, showArchived]);

  if (loading) {
    return (
      <main className="container">
        <p>Loading…</p>
      </main>
    );
  }

  if (!session) {
    return null;
  }

  async function handleSignOut() {
    await signOut();
    navigate("/");
  }

  async function handleArchive(reportId: string, currentlyArchived: boolean) {
    const res = await supabase
      .from("reports")
      .update({ archived: !currentlyArchived })
      .eq("report_id", reportId);
    if (res.error) {
      // Best-effort UX — surface the error briefly. Without a toast system we
      // just refresh the list and rely on the row staying visible if the
      // update silently failed.
      setReportsError(res.error.message);
      return;
    }
    // Optimistic update.
    setReports((prev) =>
      prev
        ? prev.map((r) =>
            r.report_id === reportId ? { ...r, archived: !currentlyArchived } : r
          )
        : prev
    );
  }

  return (
    <main className="container">
      <header
        className="brand-header"
        style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}
      >
        <div>
          <h1>Your dashboard</h1>
          {profile && (
            <p className="tagline">
              Welcome back, {profile.full_name} ({profile.school_name})
            </p>
          )}
          {!profile && !profileError && <p className="tagline">Loading your profile…</p>}
          {profileError && <p className="tagline">Error loading profile: {profileError}</p>}
        </div>
        <button
          onClick={handleSignOut}
          className="cta secondary"
          style={{ fontSize: "0.9rem" }}
        >
          Sign out
        </button>
      </header>

      <section className="hero">
        <p>
          You're signed in as <code>{session.user.email}</code>.
          {profile && profile.pilot_code_redeemed && (
            <>
              {" "}
              You have pilot access: <code>{profile.pilot_code_redeemed}</code>.
            </>
          )}
        </p>

        <div className="cta-row" style={{ marginTop: "1.5rem" }}>
          <Link to="/intake/new" className="cta primary">
            + Start a new strategic plan →
          </Link>
        </div>
      </section>

      <section className="plans-section">
        <div className="plans-header">
          <h2>Your strategic plans</h2>
          <label className="toggle-archive">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            <span>Show archived</span>
          </label>
        </div>

        {reportsError && (
          <p className="error-banner">Couldn't load your plans: {reportsError}</p>
        )}

        {reports === null && !reportsError && (
          <p style={{ color: "var(--color-text-muted)" }}>Loading your plans…</p>
        )}

        {reports !== null && reports.length === 0 && (
          <div className="info-card">
            <p style={{ margin: 0 }}>
              You don't have any strategic plans yet. Click{" "}
              <strong>+ Start a new strategic plan</strong> above to create
              your first one.
            </p>
          </div>
        )}

        {reports !== null && reports.length > 0 && (
          <div className="plans-list">
            {reports.map((r) => {
              const statusMeta = STATUS_LABELS[r.status] || STATUS_LABELS.queued;
              const curriculumLabel = CURRICULUM_LABELS[r.evaluation_curriculum] || r.evaluation_curriculum;
              const modeLabel = OPERATING_MODE_LABELS[r.operating_mode] || r.operating_mode;
              return (
                <div key={r.report_id} className={"plan-row" + (r.archived ? " plan-row-archived" : "")}>
                  <div className="plan-row-main">
                    <div className="plan-row-title">
                      {r.nickname || curriculumLabel}
                    </div>
                    <div className="plan-row-meta">
                      {curriculumLabel} · {modeLabel} · {formatDate(r.generated_at)}
                    </div>
                  </div>
                  <div className="plan-row-actions">
                    <span className={statusMeta.className}>{statusMeta.text}</span>
                    {r.status === "complete" && (
                      <Link to={"/r/" + r.report_id} className="cta secondary plan-row-link">
                        View plan →
                      </Link>
                    )}
                    {(r.status === "queued" || r.status === "running") && (
                      <Link to={"/r/" + r.report_id} className="cta secondary plan-row-link">
                        View status
                      </Link>
                    )}
                    {r.status === "failed" && (
                      <Link to={"/r/" + r.report_id} className="cta secondary plan-row-link">
                        See error
                      </Link>
                    )}
                    <button
                      onClick={() => handleArchive(r.report_id, r.archived)}
                      className="link-button plan-row-archive"
                      title={r.archived ? "Unarchive" : "Archive"}
                    >
                      {r.archived ? "Unarchive" : "Archive"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="hero" style={{ marginTop: "2rem" }}>
        <div className="info-card">
          <h3>Want plate-level analysis?</h3>
          <p>
            forgedsight tells you whether your classroom will support cert pass.
            <strong> forgedweldscan</strong> tells you whether your students
            actually will — evaluating every weld against the same acceptance
            logic industrial inspectors use.
          </p>
          <p>
            <strong>$1,500/year/school, up to 100 students.</strong>
          </p>
        </div>
      </section>

      <section className="footer-meta">
        <p>
          DEPLOY010 — v0.10.0 — Cross-product bridge with forgedweldscan live.
        </p>
      </section>
    </main>
  );
}
